import * as React from 'react';
import type { UserFlow } from '../../../MedicareProAPI/Entities/User/UserFlow';
import { FlowNavSingle } from './FlowNavSingle';

interface FlowsBrowserProps {
  flows: UserFlow[];
  selectedFlowId?: number;
  onNewFlow: () => void;
  onSelect: (flow: UserFlow) => void;
  currentUserId: number;
}

export const FlowsBrowser = ({
  flows,
  selectedFlowId,
  onNewFlow,
  onSelect,
  currentUserId,
}: FlowsBrowserProps) => {
  const [flowTypeSelected, setFlowTypeSelected] = React.useState<'owner' | 'shared'>('owner');

  const getOwnerId = (f: any) => f.getUser().getUserId();

  const { myFlows, sharedFlows } = React.useMemo(() => {
    const mine: UserFlow[] = [];
    const shared: UserFlow[] = [];
    for (const f of flows ?? []) {
      (getOwnerId(f) === currentUserId ? mine : shared).push(f);
    }
    return { myFlows: mine, sharedFlows: shared };
  }, [flows, currentUserId]);

  // Pick the correct tab based on selection/availability
  React.useEffect(() => {
    // If a flow is selected, match its owner
    if (selectedFlowId != null) {
      const sel = flows?.find((f) => f.getId() === selectedFlowId);
      if (sel) {
        setFlowTypeSelected(getOwnerId(sel) === currentUserId ? 'owner' : 'shared');
        return;
      }
    }
    // Otherwise, prefer 'owner' if it exists, else 'shared'
    if (myFlows.length > 0) setFlowTypeSelected('owner');
    else if (sharedFlows.length > 0) setFlowTypeSelected('shared');
  }, [selectedFlowId, flows, myFlows.length, sharedFlows.length, currentUserId]);

  const showOwnerBadge = myFlows.length > 0;
  const showSharedBadge = sharedFlows.length > 0;
  const showBadgeRow = showOwnerBadge && showSharedBadge;

  const list = flowTypeSelected === 'owner' ? myFlows : sharedFlows;

  return (
    <>
      <div
        onClick={onNewFlow}
        className="btn btn-sm border border-primary text-primary mb-2 hideAdd"
      >
        <i className="fas fa-plus-circle mr-2" /> New Flow
      </div>

      {showBadgeRow && (
        <div className="mb-2">
          {showOwnerBadge && (
            <span
              className={`badge badge-pill mr-2 cp ${flowTypeSelected === 'owner' ? 'badge-success' : 'badge-light text-dark'}`}
              style={{ fontSize: '8pt' }}
              onClick={() => setFlowTypeSelected('owner')}
            >
              My Flows {myFlows.length ? `(${myFlows.length})` : ''}
            </span>
          )}
          {showSharedBadge && (
            <span
              className={`badge badge-pill mr-2 cp ${flowTypeSelected === 'shared' ? 'badge-success' : 'badge-light text-dark'}`}
              style={{ fontSize: '8pt' }}
              onClick={() => setFlowTypeSelected('shared')}
            >
              Shared {sharedFlows.length ? `(${sharedFlows.length})` : ''}
            </span>
          )}
        </div>
      )}

      {list.length > 0 ? (
        list.map((item) => (
          <FlowNavSingle
            key={`flow-browser-item-${item.getId()}`}
            flow={item}
            selected={selectedFlowId === item.getId()}
            onClick={onSelect}
          />
        ))
      ) : (
        <div className="text-muted small">No flows yet.</div>
      )}
    </>
  );
};
